/**
 * Market-specific variant blocking — race-condition guard.
 *
 * Button text and disabled state are handled entirely by the Liquid
 * server-side re-render (buy-buttons.liquid / product-sticky-add-to-cart.liquid).
 * This script does NOT manipulate button DOM directly.
 *
 * Instead it solves a theme-level race condition: when a user clicks two
 * options in rapid succession, the theme fires two concurrent fetches.
 * Whichever response arrives LAST overwrites the DOM — which may be the
 * WRONG variant's HTML. This script detects that mismatch and re-triggers
 * the correct selection so the Liquid re-render can produce the right output.
 *
 * It also exposes a `data-market-blocked` attribute on <variant-picker>
 * for any third-party scripts that need to query the blocked state.
 */
(function () {
  'use strict';

  var lastIntendedFormId = null;
  var lastIntendedValues = null;
  var verifyTimer = null;

  function getCheckedOptionValues(form) {
    return Array.from(
      form.querySelectorAll('input[data-option-position]:checked')
    )
      .sort(function (a, b) {
        return (
          parseInt(a.getAttribute('data-option-position')) -
          parseInt(b.getAttribute('data-option-position'))
        );
      })
      .map(function (el) {
        return el.value;
      })
      .join(',');
  }

  /**
   * Capture the user's real intent on every option click.
   * Runs in the capturing phase, BEFORE the theme's async fetch starts,
   * so the checked radios still reflect what the user actually chose.
   */
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input.matches || !input.matches('input[data-option-position]')) return;

      var formId = input.getAttribute('form');
      if (!formId) return;
      var form = document.getElementById(formId);
      if (!form) return;

      lastIntendedFormId = formId;
      lastIntendedValues = getCheckedOptionValues(form);
    },
    true
  );

  function getBlockedIds(formId) {
    var picker = document.querySelector(
      'variant-picker[form-id="' + formId + '"]'
    );
    if (!picker) return [];
    var script = picker.querySelector('script[data-blocked-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  /**
   * Toggle visibility of elements marked data-market-hideable (e.g. @app
   * blocks like subscription widgets). These elements are NOT part of the
   * theme's partial re-render, so we must show/hide them via JS.
   */
  function toggleHideableBlocks(formId, isBlocked) {
    var rerender = document.querySelector(
      'product-rerender[observe-form="' + formId + '"]'
    );
    if (!rerender) return;
    rerender.querySelectorAll('[data-market-hideable]').forEach(function (el) {
      el.hidden = isBlocked;
    });
  }

  /**
   * After each variant:change (which fires post-rerender), schedule a
   * verification. The debounce ensures we only act once all concurrent
   * server responses have settled.
   */
  document.addEventListener('variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    var form = event.target;
    var formId = form && form.getAttribute('id');

    if (formId && variant) {
      var blockedIds = getBlockedIds(formId);
      var isBlocked = blockedIds.indexOf(variant.id) !== -1;

      var picker = document.querySelector(
        'variant-picker[form-id="' + formId + '"]'
      );
      if (picker) {
        picker.toggleAttribute('data-market-blocked', isBlocked);
      }

      toggleHideableBlocks(formId, isBlocked);
    }

    clearTimeout(verifyTimer);
    verifyTimer = setTimeout(verifySelection, 150);
  });

  /**
   * Compare the DOM's current checked options against the user's last
   * recorded intent. If they diverge a stale response won the race and
   * we re-select the intended options to trigger a fresh, correct render.
   */
  function verifySelection() {
    if (!lastIntendedValues || !lastIntendedFormId) return;

    var form = document.getElementById(lastIntendedFormId);
    if (!form) return;

    var currentValues = getCheckedOptionValues(form);

    if (currentValues !== lastIntendedValues) {
      var intended = lastIntendedValues.split(',');
      intended.forEach(function (val) {
        var input = form.querySelector(
          'input[data-option-position][value="' + val + '"]'
        );
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    lastIntendedValues = null;
  }
})();
